import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Calendar from './calendar';
import CalendarListView from './CalendarListView';
import TodoForm from './todo/TodoForm';
import MobileAuthSync from './MobileAuthSync';
import DateDetailDrawer from './DateDetailDrawer';
import GroupFilter from './GroupFilter';
import Settings, { SettingsHandle } from './Settings';
import type { Todo } from '@ext-bookmark/shared/types';

/** 顶层主视图：日历（月视图）| 待办（单一列表） */
type MainView = 'calendar' | 'todos';
import type { Holiday } from '../services/todo/holidays';
import { toggleTodoComplete, getTodosInDateRange, getTodosInDateRangeFromList, getTodos, saveTodo, isSameCalendarDay, getBaseTodoId } from '../services/todo/storage';
import { getRecurrenceTypeLabel } from '../services/todo/recurrence';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useTodosQuery } from '../hooks/queries/useTodoQueries';
import { useGroupsQuery } from '../hooks/queries/useGroupQueries';
import { markTodoUpdatedByUs } from '../services/todo/lastTodoUpdateByUs';

interface CalendarViewProps {
  onClose?: () => void;
  isMobile?: boolean;
  /** 移动端：返回书签回调，传入时在顶栏左侧显示返回按钮 */
  onBack?: () => void;
}

type ViewState = 'calendar' | 'todoForm' | 'settings' | 'authSync' | 'drawer';

const CalendarView: React.FC<CalendarViewProps> = ({ onClose, isMobile = false, onBack }) => {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const [showTodoForm, setShowTodoForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | undefined>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date()); // 默认选中今天
  const [dataVersion, setDataVersion] = useState(0); // 数据版本号，用于触发组件重新加载数据
  const [mainView, setMainView] = useState<MainView>('calendar'); // 日历 | 待办
  const [showAuthSync, setShowAuthSync] = useState(false);
  const [drawerDate, setDrawerDate] = useState<Date | null>(null);
  const [drawerHoliday, _setDrawerHoliday] = useState<Holiday | undefined>(undefined);
  void _setDrawerHoliday;
  const [showDrawer, setShowDrawer] = useState(false);
  
  // 分组筛选相关状态（支持多选）
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const { data: groups = [], isLoading: groupsLoading, isError: groupsError, error: groupsErrorDetail } = useGroupsQuery({ enabled: !!user });
  const { data: todosData, isLoading: todosLoading, isError: todosError, error: todosErrorDetail } = useTodosQuery({ enabled: !!user });
  const isInitialLoading = !!user && (todosLoading || groupsLoading) && !todosData && groups.length === 0;
  const [loadingTimeout, setLoadingTimeout] = useState(false); // 加载超时后强制显示日历，避免长时间卡在加载
  /** 勾选后短时间窗内不因 setQueryData 触发日历整月重载，避免整表闪烁 */
  const skipTodosReloadUntilRef = useRef(0);
  /** 在 skip 窗内传给日历的稳定引用，避免整表重渲染，只保留被勾选那一格已通过 setMonthTodos 的更新 */
  const prevTodosForCalendarRef = useRef<Todo[] | null>(null);
  /** 切换时实时获取最新 todos，避免闭包滞后导致基于错误状态切换 */
  const getCurrentTodosForToggle = useCallback(() => {
    return queryClient.getQueryData<Todo[]>(['todos']) ?? undefined;
  }, [queryClient]);
  /** 勾选时立即乐观更新缓存，确保切换日期时右侧面板显示正确 */
  const onOptimisticToggle = useCallback((todoId: string, newCompleted: boolean, instanceDate?: number) => {
    queryClient.setQueryData<Todo[]>(['todos'], (prev) => {
      if (!prev) return prev ?? [];
      const base = prev.find((t) => t.id === todoId);
      if (!base) return prev;
      if (base.recurrence && instanceDate !== undefined) {
        const d = new Date(instanceDate);
        d.setHours(0, 0, 0, 0);
        const ts = d.getTime();
        const cd = base.recurrence.completedDates || [];
        const newCd = newCompleted
          ? [...cd.filter((x) => !isSameCalendarDay(x, ts)), ts]
          : cd.filter((x) => !isSameCalendarDay(x, ts));
        return prev.map((t) =>
          t.id === todoId ? { ...t, recurrence: { ...t.recurrence!, completedDates: newCd } } : t
        );
      }
      return prev.map((t) => (t.id === todoId ? { ...t, completed: newCompleted } : t));
    });
  }, [queryClient]);
  /** 勾选成功后局部更新 React Query 缓存；右侧面板由 selectedDateTodosDerived 自动同步；原地 map 保持顺序 */
  const onTodoUpdated = useCallback((updatedTodo: Todo) => {
    skipTodosReloadUntilRef.current = Date.now() + 1500;
    markTodoUpdatedByUs(updatedTodo.id);
    queryClient.setQueryData<Todo[]>(['todos'], (prev) => {
      if (!prev) return prev ?? [];
      const idx = prev.findIndex((t) => t.id === updatedTodo.id);
      if (idx < 0) return prev;
      return prev.map((t, i) => (i === idx ? updatedTodo : t));
    });
  }, [queryClient]);
  const queryError = todosError ? todosErrorDetail : groupsError ? groupsErrorDetail : null;
  const [showSettings, setShowSettings] = useState(false); // 显示设置界面
  
  // Settings 组件的 ref（用于调用内部返回方法）
  const settingsRef = useRef<SettingsHandle>(null);
  const selectedGroupIdsRef = useRef<Set<string>>(new Set());
  
  // 路由栈管理（移动端原生返回支持）
  const [viewStack, setViewStack] = useState<ViewState[]>(['calendar']);
  
  // 获取当前视图状态
  const getCurrentViewState = (): ViewState => {
    if (showDrawer) return 'drawer';
    if (showTodoForm) return 'todoForm';
    if (showSettings) return 'settings';
    if (showAuthSync) return 'authSync';
    return 'calendar';
  };
  
  // 处理返回操作
  const handleBack = useCallback(() => {
    if (viewStack.length > 1) {
      const currentView = viewStack[viewStack.length - 1];
      
      // 如果当前是 settings 视图，先检查 Settings 内部是否还有子页面
      if (currentView === 'settings' && settingsRef.current) {
        const handled = settingsRef.current.handleBack();
        if (handled) {
          // Settings 内部已处理返回（还有子页面），不需要关闭 Settings
          // 不需要更新 viewStack，因为 Settings 还在显示，只是内部页面变化了
          return;
        }
        // Settings 已到最顶层（列表页），继续执行关闭逻辑
      }
      
      // 弹出当前视图
      const newStack = [...viewStack];
      newStack.pop();
      setViewStack(newStack);
      const previousView = newStack[newStack.length - 1];
      
      // 根据返回的视图状态恢复UI
      setShowDrawer(false);
      setShowTodoForm(false);
      
      // 如果当前关闭的是设置页面，清除按钮焦点状态
      if (currentView === 'settings') {
        setShowSettings(false);
        // 使用 setTimeout 确保在 DOM 更新后清除焦点
        setTimeout(() => {
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }, 0);
      } else {
        setShowSettings(false);
      }
      
      setShowAuthSync(false);
      
      if (previousView === 'drawer') {
        setShowDrawer(true);
      } else if (previousView === 'todoForm') {
        setShowTodoForm(true);
      } else if (previousView === 'settings') {
        setShowSettings(true);
      } else if (previousView === 'authSync') {
        setShowAuthSync(true);
      }
    } else if (onClose) {
      // 如果栈为空（已经在日历/列表页），关闭整个日历视图（退出应用）
      onClose();
    }
  }, [viewStack, onClose]);

  
  // 导航到新视图
  const navigateToView = (newView: ViewState) => {
    const currentView = getCurrentViewState();
    if (currentView !== newView) {
      setViewStack([...viewStack, newView]);
    }
  };
  
  // 监听浏览器返回按钮和 Android 系统返回按钮（移动端）
  useEffect(() => {
    if (!isMobile) return;
    
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      handleBack();
    };
    
    // 监听 popstate 事件（浏览器返回按钮）
    window.addEventListener('popstate', handlePopState);
    
    // 监听 Android 系统返回按钮
    let backButtonListener: any = null;
    const setupBackButton = async () => {
      try {
        const { App } = await import('@capacitor/app');
        backButtonListener = await App.addListener('backButton', () => {
          handleBack();
        });
      } catch (error) {
        // Capacitor App API 不可用时，忽略错误
        console.warn('[CalendarView] App API not available for back button:', error);
      }
    };
    setupBackButton();
    
    // 当视图变化时，更新历史记录
    const currentView = getCurrentViewState();
    if (currentView !== 'calendar') {
      window.history.pushState({ view: currentView }, '', window.location.href);
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [isMobile, showDrawer, showTodoForm, showSettings, showAuthSync, handleBack]);
  
  // 当视图变化时更新栈
  useEffect(() => {
    const currentView = getCurrentViewState();
    if (viewStack[viewStack.length - 1] !== currentView && currentView !== 'calendar') {
      navigateToView(currentView);
    }
  }, [showDrawer, showTodoForm, showSettings, showAuthSync]);

  // 同步 selectedGroupIdsRef
  useEffect(() => {
    selectedGroupIdsRef.current = selectedGroupIds;
  }, [selectedGroupIds]);

  // 加载分组列表（使用新的缓存策略：先显示缓存，异步更新）
  // 分组首次加载时默认选中所有分组
  useEffect(() => {
    if (groups.length > 0) {
      setSelectedGroupIds(prev => (prev.size === 0 ? new Set(groups.map(g => g.id)) : prev));
    }
  }, [groups]);

  // 不再将 todosData 回写 localStorage：已登录时以 Supabase + Realtime 为唯一数据源，未登录时 getFilteredTodos 走 localStorage

  // 缓存个人分组查找结果
  const personalGroup = useMemo(() => {
    return groups.find(g => g.isPersonal);
  }, [groups]);

  // 已登录：从 todosData 同步派生选中日待办，保证与日期格、缓存完全一致
  // 未选分组(selectedGroupIds.size===0) 视为「不筛选」，显示全部待办
  const selectedDateTodosDerived = useMemo(() => {
    if (!selectedDate || !user || todosData == null) return null;
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);
    let todos = getTodosInDateRangeFromList(todosData, startOfDay, endOfDay);
    if (selectedGroupIds.size > 0) {
      todos = todos.filter(todo => {
        if (!todo.groupId) return personalGroup != null && selectedGroupIds.has(personalGroup.id);
        return selectedGroupIds.has(todo.groupId);
      });
    }
    return todos;
  }, [selectedDate, user, todosData, selectedGroupIds, personalGroup]);

  // 未登录：异步从 localStorage 加载
  const [selectedDateTodosOffline, setSelectedDateTodosOffline] = useState<Todo[]>([]);
  const getFilteredTodos = useCallback(async (date?: Date): Promise<Todo[]> => {
    let todos: Todo[] = [];
    if (date) {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      todos = await getTodosInDateRange(startOfDay, endOfDay);
    } else {
      todos = await getTodos();
    }
    if (selectedGroupIds.size > 0) {
      todos = todos.filter(todo => {
        if (!todo.groupId) return personalGroup != null && selectedGroupIds.has(personalGroup.id);
        return selectedGroupIds.has(todo.groupId);
      });
    }
    return todos;
  }, [selectedGroupIds, personalGroup]);

  useEffect(() => {
    if (user != null && todosData != null) return;
    if (selectedDate) {
      getFilteredTodos(selectedDate).then(setSelectedDateTodosOffline);
    } else {
      setSelectedDateTodosOffline([]);
    }
  }, [selectedDate, selectedGroupIds, groups, user, todosData, getFilteredTodos]);

  const selectedDateTodos = (user != null && todosData != null && selectedDateTodosDerived != null)
    ? selectedDateTodosDerived
    : selectedDateTodosOffline;

  const handleTodoClick = useCallback((todo: Todo) => {
    setEditingTodo(todo);
    setShowTodoForm(true);
    if (isMobile) {
      navigateToView('todoForm');
    }
  }, [isMobile]);

  const handleDateClick = useCallback(async (date: Date) => {
    // 选中日期
    setSelectedDate(date);
    
    // 移动端：不再打开抽屉，因为 Calendar 组件已经内置了待办事项显示
    // 如果需要查看详细信息，可以长按日期
  }, []);

  const handleDateDoubleClick = useCallback((date: Date) => {
    // 双击/长按日期：选中日期并打开新增待办事项表单
    setSelectedDate(date);
    setEditingTodo(undefined);
    setShowTodoForm(true);
    if (isMobile) {
      navigateToView('todoForm');
    }
  }, [isMobile]);

  const handleAddTodo = useCallback(() => {
    setEditingTodo(undefined);
    setSelectedDate(new Date());
    setShowTodoForm(true);
    if (isMobile) {
      navigateToView('todoForm');
    }
  }, [isMobile]);

  /** 待办视图快捷添加：按标题和 startDate（0=无日期）创建待办，默认分组为当前选中或个人 */
  const handleQuickAddTodo = useCallback(async (title: string, startDate: number) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const groupId =
      (selectedGroupIds.size === 1 ? [...selectedGroupIds][0] : null) ||
      personalGroup?.id ||
      (groups && groups.length > 0 ? groups[0].id : undefined);
    if (!groupId) return;
    const now = Date.now();
    const newTodo: Todo = {
      id: `todo_${now}_${Math.random().toString(36).slice(2, 11)}`,
      title: trimmed,
      completed: false,
      startDate,
      createdAt: now,
      updatedAt: now,
      groupId,
      createdBy: user?.id,
    };
    try {
      const saved = await saveTodo(newTodo);
      if (user) {
        markTodoUpdatedByUs(saved.id);
        queryClient.setQueryData<Todo[]>(['todos'], (old) => {
          if (!old) return old;
          const i = old.findIndex((t) => t.id === saved.id);
          if (i >= 0) return old.map((t, idx) => (idx === i ? saved : t));
          return [saved, ...old];
        });
      }
      setDataVersion((v) => v + 1);
    } catch (e) {
      console.error('快捷添加失败', e);
    }
  }, [selectedGroupIds, personalGroup?.id, groups, user, queryClient]);

  const handleSaveTodo = useCallback(async (savedTodo?: Todo, opts?: { deleted?: boolean }) => {
    setShowTodoForm(false);
    setEditingTodo(undefined);
    if (isMobile && viewStack.length > 1) {
      setViewStack(viewStack.slice(0, -1));
    }
    if (!selectedDate) {
      setSelectedDate(new Date());
    }
    // 已登录时 TodoForm 已更新缓存，selectedDateTodosDerived 自动同步；未登录时需手动更新
    if (savedTodo !== undefined && !user) {
      if (opts?.deleted) {
        setSelectedDateTodosOffline((prev) => prev.filter((t) => t.id !== savedTodo.id));
      } else {
        setSelectedDateTodosOffline((prev) => {
          const i = prev.findIndex((t) => t.id === savedTodo.id);
          if (i >= 0) return prev.map((t) => (t.id === savedTodo.id ? savedTodo : t));
          return [...prev, savedTodo];
        });
      }
    } else if (!user && selectedDate) {
      getFilteredTodos(selectedDate).then(setSelectedDateTodosOffline);
    }
    setDataVersion((prev) => prev + 1);
  }, [isMobile, viewStack, selectedDate, user, getFilteredTodos]);

  // 加载超时保护：超过 3 秒强制显示日历，避免用户误以为卡住而按浏览器返回
  useEffect(() => {
    if (isInitialLoading) {
      setLoadingTimeout(false);
      const t = setTimeout(() => setLoadingTimeout(true), 3000);
      return () => clearTimeout(t);
    }
    setLoadingTimeout(false);
  }, [isInitialLoading]);

  if (isInitialLoading && !loadingTimeout) {
    return (
      <div className={`h-full flex flex-col overflow-hidden ${isMobile ? 'p-0' : 'p-4'}`}>
        <div className={`${isMobile ? 'w-full h-full' : 'max-w-[1600px] mx-auto w-full'} flex flex-1 flex-col items-center justify-center`}>
          <p className="text-gray-500 dark:text-gray-400">加载中...</p>
          <p className="text-xs text-gray-400 mt-2">若长时间无响应，约 3 秒后自动进入</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col overflow-hidden ${isMobile ? 'p-0' : 'p-4 bg-gradient-to-br from-background via-background to-muted/20 dark:to-muted/30'}`}>
      <div className={`${isMobile ? 'w-full h-full' : 'max-w-[1600px] mx-auto w-full'} flex flex-col flex-1 min-h-0`}>
        {queryError && (
          <div className="flex-shrink-0 px-3 py-2 glass rounded-xl text-destructive text-sm mx-2 mt-2 border-destructive/30">
            {queryError instanceof Error ? queryError.message : '加载数据失败，请重试'}
          </div>
        )}
        {isMobile ? (
          <>
            {/* 移动端布局 */}
            {/* 顶部栏：返回 | 头像 + 居中视图切换 | 设置 */}
            <div className="flex items-center justify-between px-3 pt-2 pb-2 flex-shrink-0 glass border-b border-border/50 gap-2">
              {/* 左侧：返回书签 + 头像 */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {onBack ? (
                  <button
                    onClick={onBack}
                    className="p-2 -ml-1 rounded-lg hover:bg-muted transition-colors min-h-[40px] min-w-[40px] touch-manipulation flex items-center justify-center"
                    title="返回书签"
                    aria-label="返回书签"
                  >
                    <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                ) : null}
                <button
                  onClick={() => setShowAuthSync(true)}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={user ? '账号与同步' : '登录'}
                >
                  {user ? (
                    <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm shadow-sm">
                    {profile?.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                </button>
              </div>

              {/* 居中视图切换：日历 | 待办 */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1 shadow-sm">
                  <button
                    onClick={() => setMainView('calendar')}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 cursor-pointer ${
                      mainView === 'calendar'
                        ? 'bg-background/90 text-foreground font-medium shadow-sm'
                        : 'text-muted-foreground'
                    }`}
                  >
                    日历
                  </button>
                  <button
                    onClick={() => setMainView('todos')}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 cursor-pointer ${
                      mainView === 'todos'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm font-medium'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    待办
                  </button>
                </div>
              </div>

               {/* 右侧：设置按钮 */}
               <button
                 onClick={() => {
                   setShowSettings(true);
                   if (isMobile) {
                     navigateToView('settings');
                   }
                 }}
                 className="p-2 rounded-full hover:bg-accent/80 transition-colors duration-200 cursor-pointer"
                 title="设置"
               >
                 <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                 </svg>
               </button>
            </div>

            {/* 分组筛选器 - 移动端，所有视图共用 */}
            {user && (
              <div className="flex-shrink-0 border-b border-border/50 glass">
                <GroupFilter
                  selectedGroupIds={selectedGroupIds}
                  onGroupIdsChange={setSelectedGroupIds}
                  onCreateGroup={() => {
                    setShowSettings(true);
                    navigateToView('settings');
                  }}
                  isMobile={isMobile}
                />
              </div>
            )}

            {/* 主内容区：日历（月视图）或待办（单一列表） */}
            <div className="flex-1 min-h-0 flex flex-col">
              {mainView === 'todos' ? (
                <CalendarListView
                  key={`todos-${dataVersion}`}
                  selectedDate={selectedDate}
                  onTodoClick={handleTodoClick}
                  onDateClick={handleDateClick}
                  onDateDoubleClick={handleDateDoubleClick}
                  isMobile={isMobile}
                  selectedGroupIds={selectedGroupIds}
                  todosForToggle={todosData ?? undefined}
                  getCurrentTodosForToggle={getCurrentTodosForToggle}
                  onTodoUpdated={onTodoUpdated}
                />
              ) : (
                <Calendar
                  key={`month-${dataVersion}`}
                  view="month"
                  selectedDate={selectedDate}
                  onTodoClick={handleTodoClick}
                  onDateClick={handleDateClick}
                  onDateDoubleClick={handleDateDoubleClick}
                  isMobile={isMobile}
                  selectedGroupIds={selectedGroupIds}
                  personalGroupId={personalGroup?.id ?? undefined}
                  dataVersion={dataVersion}
                  selectedDateTodos={isMobile ? selectedDateTodos : undefined}
                  onSelectedDateTodosChange={isMobile && !user ? setSelectedDateTodosOffline : undefined}
                  onOptimisticToggle={onOptimisticToggle}
                  userId={user?.id}
                  getCurrentTodosForToggle={getCurrentTodosForToggle}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {/* 桌面端布局：方案 C 卡片化，玻璃 + 阴影 + 圆角 */}
            <div className="glass-card rounded-2xl shadow-lg flex flex-col flex-1 min-h-0 overflow-hidden p-4">
            {/* 顶部栏：标题 | 设置、视图切换、新增待办 — 视图切换与设置齐平，减少对日历区域占用 */}
            <div className="flex items-center justify-between flex-shrink-0 pb-4 border-b border-border/30">
              <h1 className="text-2xl font-bold text-foreground">日历</h1>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                   setShowSettings(true);
                   if (isMobile) {
                     navigateToView('settings');
                   }
                 }}
                  className="p-2 rounded-full hover:bg-accent/80 transition-colors duration-200 cursor-pointer"
                  title="设置"
                >
                  <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                {/* 视图切换 - 日历 | 待办 */}
                <div className="inline-flex items-center gap-0.5 rounded-xl bg-black/[0.04] dark:bg-white/[0.06] p-0.5">
                  <button
                    onClick={() => setMainView('calendar')}
                    className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 font-medium cursor-pointer ${
                      mainView === 'calendar'
                        ? 'bg-background/90 text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    日历
                  </button>
                  <button
                    onClick={() => setMainView('todos')}
                    className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 font-medium cursor-pointer ${
                      mainView === 'todos'
                        ? 'bg-background/90 text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    待办
                  </button>
                </div>
                <button
                  onClick={handleAddTodo}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  新增待办
                </button>
                {onClose && (
                  <button
                    onClick={onClose}
                    className="px-4 py-2 glass text-foreground rounded-xl text-sm font-medium hover:opacity-90 transition-all duration-200 cursor-pointer"
                  >
                    关闭
                  </button>
                )}
              </div>
            </div>

            {/* 分组筛选器 - 桌面端，与整页同表面 */}
            {user && (
              <div className="flex-shrink-0 border-b border-border/30 py-1">
                <GroupFilter
                  selectedGroupIds={selectedGroupIds}
                  onGroupIdsChange={setSelectedGroupIds}
                  onCreateGroup={() => {
                    setShowSettings(true);
                    if (isMobile) {
                      navigateToView('settings');
                    }
                  }}
                  isMobile={isMobile}
                />
              </div>
            )}


        {/* 主内容区域：日历主视图为左日历+右待办，待办主视图为整区列表 */}
        <div className="flex-1 min-h-0 flex gap-4">
          {/* 左侧：日历（月视图）或待办（单一列表） */}
          <div className="flex-1 min-h-0 flex flex-col min-w-0">
            {mainView === 'todos' ? (
              <CalendarListView
                key={`todos-${dataVersion}`}
                selectedDate={selectedDate}
                selectedGroupIds={selectedGroupIds}
                onTodoClick={handleTodoClick}
                onDateClick={handleDateClick}
                onDateDoubleClick={handleDateDoubleClick}
                onQuickAdd={handleQuickAddTodo}
                isMobile={isMobile}
                todosForToggle={todosData ?? undefined}
                getCurrentTodosForToggle={getCurrentTodosForToggle}
                onTodoUpdated={onTodoUpdated}
              />
            ) : (
            <Calendar
              key={`month-${dataVersion}`}
              view="month"
              selectedDate={selectedDate}
              selectedGroupIds={selectedGroupIds}
              personalGroupId={personalGroup?.id ?? undefined}
              dataVersion={dataVersion}
              todosForCalendar={
                Date.now() < skipTodosReloadUntilRef.current && prevTodosForCalendarRef.current !== null
                  ? prevTodosForCalendarRef.current
                  : (prevTodosForCalendarRef.current = todosData ?? null)
              }
              onTodoClick={handleTodoClick}
              onDateClick={handleDateClick}
              onDateDoubleClick={handleDateDoubleClick}
              onTodosInvalidate={() => queryClient.invalidateQueries({ queryKey: ['todos'] })}
              onTodoUpdated={onTodoUpdated}
              onOptimisticToggle={onOptimisticToggle}
              skipTodosReloadUntilRef={skipTodosReloadUntilRef}
              isMobile={isMobile}
              getCurrentTodosForToggle={getCurrentTodosForToggle}
            />
            )}
          </div>

          {/* 右侧：仅在日历主视图时显示选中日待办 */}
          {mainView === 'calendar' && (
          <div className="w-80 flex-shrink-0 flex flex-col overflow-hidden min-h-[60vh] border-l border-border pl-4">
              <div className="flex-shrink-0 mb-3 pb-3 border-b border-border/30">
                <h3 className="text-base font-semibold text-foreground">
                  {selectedDate ? selectedDate.toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long',
                  }) : '请选择日期'}
                </h3>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {!selectedDate ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      请选择一个日期查看待办事项
                    </p>
                  </div>
                ) : selectedDateTodos.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      这一天没有待办事项
                    </p>
                    <button
                      onClick={() => handleDateDoubleClick(selectedDate)}
                      className="text-sm text-primary hover:underline cursor-pointer"
                    >
                      点击添加待办事项
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDateTodos.map((todo) => {
                      const originalTodoId = getBaseTodoId(todo);
                      const recurrenceLabel = todo.recurrence 
                        ? getRecurrenceTypeLabel(todo.recurrence.type, todo.recurrence)
                        : null;
                      
                      // 判断是否为重复事项实例
                      const isRecurrenceInstance = !!todo.recurrence && todo.id !== originalTodoId;
                      // 如果是重复事项实例，提取实例日期
                      const instanceDate = isRecurrenceInstance ? todo.startDate : undefined;
                      
                      return (
                        <div
                          key={todo.id}
                          className="glass-hover rounded-xl border border-border/50 transition-colors duration-200 p-3 cursor-pointer"
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={todo.completed}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newCompleted = !todo.completed;
                                if (user && todosData != null) {
                                  queryClient.setQueryData<Todo[]>(['todos'], (prev) => {
                                    if (!prev) return prev;
                                    const base = prev.find(t => t.id === originalTodoId);
                                    if (!base || !base.recurrence || instanceDate === undefined) {
                                      return prev.map(t => t.id === originalTodoId ? { ...t, completed: newCompleted } : t);
                                    }
                                    const dateTs = new Date(instanceDate);
                                    dateTs.setHours(0, 0, 0, 0);
                                    const ts = dateTs.getTime();
                                    const cd = base.recurrence.completedDates || [];
                                    const newCd = newCompleted ? [...cd.filter(d => !isSameCalendarDay(d, ts)), ts] : cd.filter(d => !isSameCalendarDay(d, ts));
                                    return prev.map(t => (t.id === originalTodoId ? { ...t, recurrence: { ...t.recurrence!, completedDates: newCd } } : t));
                                  });
                                } else {
                                  setSelectedDateTodosOffline(prev => prev.map(t => t.id === todo.id ? { ...t, completed: newCompleted } : t));
                                }
                                setDataVersion(prev => prev + 1);
                                (async () => {
                                  try {
                                    const updated = await toggleTodoComplete(originalTodoId, instanceDate, {
                                      getCurrentTodos: getCurrentTodosForToggle,
                                      currentInstanceCompleted: todo.completed,
                                    });
                                    if (updated) {
                                      if (!user) {
                                        setSelectedDateTodosOffline(prev => prev.map(t => t.id !== todo.id ? t : {
                                          ...t,
                                          completed: updated.recurrence && instanceDate !== undefined
                                            ? (updated.recurrence.completedDates || []).some(d => isSameCalendarDay(d, new Date(instanceDate).setHours(0, 0, 0, 0)))
                                            : !!updated.completed,
                                          recurrence: updated.recurrence ?? t.recurrence,
                                        }));
                                      }
                                      onTodoUpdated(updated);
                                    }
                                  } catch (error) {
                                    console.error('[CalendarView] 切换待办事项状态失败:', error);
                                    if (user) {
                                      queryClient.invalidateQueries({ queryKey: ['todos'] });
                                    } else if (selectedDate) {
                                      getFilteredTodos(selectedDate).then(setSelectedDateTodosOffline);
                                    }
                                    setDataVersion(prev => prev + 1);
                                  }
                                })();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              className="ios-checkbox mt-1"
                            />
                            <div 
                              className="flex-1 min-w-0 cursor-pointer"
                              onDoubleClick={isMobile ? undefined : () => handleTodoClick(todo)}
                              onClick={isMobile ? () => handleTodoClick(todo) : undefined}
                              title={isMobile ? "单击编辑待办事项" : "双击编辑待办事项"}
                            >
                              {/* 标题 */}
                              <div
                                className={`text-base font-medium mb-1 ${
                                  todo.completed
                                    ? 'line-through text-gray-400 dark:text-gray-500'
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}
                                style={{ color: todo.fontColor && !todo.completed ? todo.fontColor : undefined }}
                              >
                                {todo.title}
                              </div>
                              
                              {/* 描述 */}
                              {todo.description && (
                                <div className={`text-sm text-gray-600 dark:text-gray-400 mb-2 ${
                                  todo.completed ? 'line-through' : ''
                                }`}>
                                  {todo.description}
                                </div>
                              )}
                              
                              {/* 分组信息 */}
                              {todo.groupId && (() => {
                                const group = groups.find(g => g.id === todo.groupId);
                                if (group) {
                                  return (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                      <span
                                        className="px-2 py-0.5 rounded-md text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                      >
                                        {group.name}
                                      </span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* 详细信息 */}
                              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                {/* 重复类型 */}
                                {recurrenceLabel && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    <span>{recurrenceLabel}</span>
                                  </div>
                                )}
                                
                                {/* 提醒 */}
                                {todo.reminder?.enabled && todo.reminder.minutesBefore && todo.reminder.minutesBefore.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    <span>
                                      提醒: {todo.reminder.minutesBefore.map((m: number) => {
                                        const absM = Math.abs(m);
                                        if (absM >= 1440) return `提前${Math.floor(absM / 1440)}天`;
                                        if (absM >= 60) return `提前${Math.floor(absM / 60)}小时`;
                                        return `提前${absM}分钟`;
                                      }).join(', ')}
                                    </span>
                                  </div>
                                )}
                                
                                {/* 时间 */}
                                {!todo.recurrence && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                      {new Date(todo.startDate).toLocaleDateString('zh-CN', {
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                    </span>
                                  </div>
                                )}
                                
                                {/* 完成时间 */}
                                {todo.completed && todo.completedAt && (
                                  <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                      完成于 {new Date(todo.completedAt).toLocaleDateString('zh-CN', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
            </div>
          </>
        )}
      </div>

      {/* 待办事项表单 */}
      {showTodoForm && (
        <TodoForm
          todo={editingTodo}
          defaultDate={selectedDate || undefined}
          defaultGroupId={selectedGroupIds.size === 1 ? Array.from(selectedGroupIds)[0] : null} // 如果只选中一个分组，新建任务时默认选择该分组
          groups={groups} // 传递预加载的分组列表，避免重复加载和页面跳动
          onClose={() => {
            setShowTodoForm(false);
            setEditingTodo(undefined);
            if (!selectedDate) {
              setSelectedDate(new Date());
            }
          }}
          onSave={handleSaveTodo}
          isMobile={isMobile}
        />
      )}

      {/* 移动端登录/同步模态框 */}
      {isMobile && showAuthSync && (
        <MobileAuthSync
          onClose={() => setShowAuthSync(false)}
        />
      )}

      {/* 移动端底部抽屉 */}
      {isMobile && (
        <DateDetailDrawer
          date={drawerDate}
          holiday={drawerHoliday}
          isOpen={showDrawer}
          onClose={() => {
            setShowDrawer(false);
            setDrawerDate(null);
            // 从路由栈中移除 drawer
            if (isMobile && viewStack.length > 1) {
              setViewStack(viewStack.slice(0, -1));
            }
          }}
          onTodoClick={handleTodoClick}
          onAddTodo={(date) => {
            setSelectedDate(date);
            setEditingTodo(undefined);
            setShowTodoForm(true);
            setShowDrawer(false);
          }}
          isMobile={isMobile}
        />
      )}

      {/* 分组管理界面 */}
      <Settings
        ref={settingsRef}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onGroupChange={() => {
          queryClient.invalidateQueries({ queryKey: ['groups'] });
        }}
        isMobile={isMobile}
      />
    </div>
  );
};

export default CalendarView;
